import type { ResultOf } from '@graphql-typed-document-node/core';
import { useEffect, useEffectEvent, useState } from 'react';
import toast from 'react-hot-toast';
import { graphql } from 'src/gql';
import { useTimeout } from 'usehooks-ts';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

const MAX_TIME_FOR_FETCHING_ACCOUNT = 1000 * 180;

// TODO: Better way to query a provider account in isolation
const CLIENT_QUERY = graphql(/* GraphQL */ `
  query ClientFromProviderAccountToast($clientUserId: ID, $householdId: ID) {
    client(id: $clientUserId) {
      providerAccounts(id: $householdId) {
        sourceStatus
        id
        providerName
        accounts {
          id
          statementsBoxFolderId
        }
      }
    }
  }
`);

type ProviderAccount = NonNullable<ResultOf<typeof CLIENT_QUERY>['client']['providerAccounts']>[number];
function findProviderAccount(id: string, data: ResultOf<typeof CLIENT_QUERY>): ProviderAccount | undefined {
  return data.client.providerAccounts?.find((providerAccount) => providerAccount.id === id);
}

function haveAccountsLoaded(providerAccount?: ProviderAccount) {
  return (
    !!providerAccount?.accounts &&
    providerAccount.accounts.length > 0 &&
    // TODO: import from gql
    providerAccount.sourceStatus === 'ACTIVE'
  );
}

function haveAccountsFullyLoaded(providerAccount?: ProviderAccount) {
  if (!providerAccount?.accounts) {
    return false;
  }

  const incompleteAccount = providerAccount.accounts.find((account) => !account.statementsBoxFolderId);

  return haveAccountsLoaded(providerAccount) && !incompleteAccount;
}

function callOnLoadedFunctions(onLoaded?: () => void, onAccountsLoaded?: () => void) {
  if (onLoaded) {
    onLoaded();
  }
  if (onAccountsLoaded) {
    onAccountsLoaded();
  }
}

const ProviderIDToToastIDMap = new Map<string, string>();

export default function ProviderAccountToast({
  providerAccountId,
  clientUserId,
  householdId,
  isReconnect,
  onAccountsLoaded,
  onPendingProviderAccount,
  onAccountsFullyLoaded,
}: {
  readonly providerAccountId: string;
  readonly clientUserId?: string;
  readonly householdId?: string;
  readonly isReconnect?: boolean;
  readonly onAccountsLoaded?: () => void;
  readonly onPendingProviderAccount?: (providerName: string | null) => void;
  readonly onAccountsFullyLoaded?: () => void;
}) {
  const { data, loading, startPolling, stopPolling } = useQueryWithErrorLogging(CLIENT_QUERY, {
    variables: { clientUserId: clientUserId || undefined, householdId },
  });

  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [pendingNotified, setPendingNotified] = useState(false);
  const [loadingToast, setLoadingToast] = useState<string | undefined>(undefined);

  useEffect(() => {
    // This is so the user can see the loading element
    // TODO: Make scroll position more precise
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // If we already have a toast for this provider account, don't create a new one
    if (ProviderIDToToastIDMap.has(providerAccountId)) {
      return;
    }

    const toastID = toast.loading(isReconnect ? `Reconnecting account(s)` : `Adding account(s)`);
    ProviderIDToToastIDMap.set(providerAccountId, toastID);
    setLoadingToast(toastID);
  }, []);

  useTimeout(() => {
    if (!accountsLoaded) {
      stopPolling();
      callOnLoadedFunctions(onAccountsLoaded, onAccountsFullyLoaded);
      toast.error('Importing accounts timed out.');
      toast.dismiss(loadingToast);
    }
  }, MAX_TIME_FOR_FETCHING_ACCOUNT);

  useEffect(() => {
    startPolling(500);
  }, [providerAccountId]);

  const handleAccountsLoaded = useEffectEvent(() => {
    onAccountsLoaded?.();
  });
  const handlePendingProviderAccount = useEffectEvent((providerName: string | null) => {
    onPendingProviderAccount?.(providerName);
  });
  const handleAccountsFullyLoaded = useEffectEvent(() => {
    onAccountsFullyLoaded?.();
  });

  useEffect(() => {
    if (loading || !data) {
      return;
    }

    const providerAccount = findProviderAccount(providerAccountId, data);

    if (onPendingProviderAccount && !pendingNotified && providerAccount?.sourceStatus === 'LOADING') {
      setPendingNotified(true);
      handlePendingProviderAccount(providerAccount.providerName ?? null);
    }

    if (providerAccount?.sourceStatus && !['ACTIVE', 'LOADING'].includes(providerAccount.sourceStatus)) {
      stopPolling();
      setAccountsLoaded(true);
      callOnLoadedFunctions(onAccountsLoaded, onAccountsFullyLoaded);
      toast.error('Error importing accounts, please try again.');
    }

    if (onAccountsLoaded && haveAccountsLoaded(providerAccount)) {
      setAccountsLoaded(true);
      stopPolling();
      handleAccountsLoaded();
      toast.success('Account(s) added!', {
        id: loadingToast,
      });
    }

    if (onAccountsFullyLoaded && haveAccountsFullyLoaded(providerAccount)) {
      setAccountsLoaded(true);
      stopPolling();
      handleAccountsFullyLoaded();

      toast.success('Account(s) added!', {
        id: loadingToast,
      });
    }
  }, [data, loading, providerAccountId]);

  if (!loading && data) {
    const providerAccount = findProviderAccount(providerAccountId, data);
    if (onAccountsLoaded && haveAccountsLoaded(providerAccount)) {
      return null;
    }
    if (onAccountsFullyLoaded && haveAccountsFullyLoaded(providerAccount)) {
      return null;
    }
  }
  return null;
}
